import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowRight, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";

function formatApiError(detail, fallback = "Something went wrong.") {
  if (!detail) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return detail?.msg || JSON.stringify(detail);
}

export default function Register() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr(null);
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/register", form);
      if (data.access_token) localStorage.setItem("session_token", data.access_token);
      setUser(data.user);
      toast.success("Account created. Next: subscribe.");
      navigate("/subscribe");
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail, "Registration failed."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-sg-paper flex flex-col">
      <header className="bg-sg-navy text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-20 flex items-center">
          <Link to="/" className="flex items-center" data-testid="brand-logo">
            <img src="/inpropportal-logo.png" alt="INPropPortal.com" className="h-11 w-auto" />
          </Link>
        </div>
      </header>
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-2">
        <div className="hidden lg:block relative overflow-hidden bg-sg-navy text-white">
          <div className="absolute inset-0 bg-cover bg-center opacity-30"
               style={{ backgroundImage: "url(https://images.pexels.com/photos/27826399/pexels-photo-27826399.jpeg)" }} />
          <div className="absolute inset-0 bg-gradient-to-tr from-sg-navy-deep via-sg-navy/80 to-transparent" />
          <div className="relative h-full flex flex-col justify-between p-12 overflow-y-auto">
            <div>
              <div className="overline text-sg-gold mb-4">JOIN INPROPPORTAL</div>
              <h2 className="font-display text-5xl leading-[1.05]">
                Your Branded Property Portal, <span className="font-serif-italic text-sg-gold">live in just 90 seconds.</span>
              </h2>
              <ul className="mt-8 space-y-4 text-sm text-white/85">
                {[
                  { title: "Your Own Professional Site", body: "A clean, branded webpage that makes you look like a pro to every client." },
                  { title: "The “Pune Knowledge Hub”", body: "Stop searching through brochures. Instant access to 135+ real-time project details across Pune’s hottest areas." },
                  { title: "AI-Powered WhatsApp Assistant", body: "When you get a floor plan or inquiry on WhatsApp, our system extracts the details and organises them for you automatically." },
                  { title: "Lead “Gatekeeper”", body: "Let our tool do the boring work of filtering out time-wasters, so you only talk to people who are actually ready to buy." },
                  { title: "Instant Financial Tools", body: "Need to show a client their EMI or Stamp Duty breakdown? Do it in seconds with our built-in calculators." },
                  { title: "Tracking & Alerts", body: "See exactly what your clients are interested in and get notified the second a new lead comes in." },
                ].map((b) => (
                  <li key={b.title} className="flex items-start gap-3" data-testid={`register-benefit-${b.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                    <Check className="w-4 h-4 text-sg-gold mt-1 shrink-0" />
                    <div className="leading-relaxed">
                      <strong className="text-white">{b.title}:</strong> <span className="text-white/75">{b.body}</span>
                    </div>
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-sm text-white/80 italic">And much more&hellip;</p>
              <p className="mt-3 text-sm text-sg-gold font-medium">In just ₹999 per month — much less than a chai &amp; vada pav a day!!</p>
            </div>
            <div className="text-xs text-white/60 font-mono mt-8">cancel anytime · GST-inclusive</div>
          </div>
        </div>
        <div className="flex items-center justify-center p-8 bg-sg-cream-soft">
          <form className="w-full max-w-sm" onSubmit={onSubmit} data-testid="register-form">
            <div className="overline text-sg-gold-deep mb-3">CREATE AGENT ACCOUNT</div>
            <h1 className="font-display text-5xl text-sg-navy">Get started.</h1>
            <p className="mt-3 text-sg-mute">Step 1 of 3 — account &middot; subscribe &middot; profile.</p>

            <div className="mt-8 space-y-4">
              <div>
                <Label htmlFor="name" className="text-xs uppercase tracking-wider text-sg-mute">Full name</Label>
                <Input id="name" required minLength={2} value={form.name} onChange={onChange("name")}
                       className="mt-1 h-12 bg-white" autoComplete="name" data-testid="register-name" />
              </div>
              <div>
                <Label htmlFor="email" className="text-xs uppercase tracking-wider text-sg-mute">Email</Label>
                <Input id="email" type="email" required value={form.email} onChange={onChange("email")}
                       className="mt-1 h-12 bg-white" autoComplete="email" data-testid="register-email" />
              </div>
              <div>
                <Label htmlFor="password" className="text-xs uppercase tracking-wider text-sg-mute">Password (8+ chars)</Label>
                <Input id="password" type="password" required minLength={8} value={form.password} onChange={onChange("password")}
                       className="mt-1 h-12 bg-white" autoComplete="new-password" data-testid="register-password" />
              </div>
              {err && (
                <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3" data-testid="register-error">
                  {err}
                </div>
              )}
              <Button type="submit" size="lg" disabled={submitting}
                      className="w-full h-12 rounded-full bg-sg-navy hover:bg-sg-navy-700 text-white"
                      data-testid="register-submit">
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Create account <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
            <p className="mt-6 text-xs text-sg-mute leading-relaxed">
              Already a member? <Link to="/login" className="text-sg-gold-deep border-b border-sg-gold" data-testid="register-login-link">Sign in</Link>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
